import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerBusiness, submitLead } from '../api';
import { useAuth } from '../context/AuthContext';
import './BusinessAuthPage.css';

const CUISINES = ['canadian','indian','chinese','italian','mexican','japanese','thai','vietnamese','middle-eastern','greek','korean','american','seafood','pizza','sushi','vegetarian','vegan','breakfast','bakery','other'];
const HOODS = ['Downtown','St. Vital','River Heights','Transcona','North End','Osborne Village','Wolseley','West End','St. James','Elmwood','Fort Garry','Tuxedo'];
const MEAL_TYPES = ['Dine-in','Takeout','Delivery'];
const PRICE_RANGES = [{v:1,l:'$ — Budget friendly'},{v:2,l:'$$ — Moderate'},{v:3,l:'$$$ — Upscale'},{v:4,l:'$$$$ — Fine dining'}];

export default function BusinessRegisterPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [lead, setLead] = useState({ business_name:'', owner_name:'', email:'', phone:'', cuisine_type:'' });
  const [form, setForm] = useState({
    email:'', password:'', confirmPassword:'',
    business_name:'', owner_name:'', phone:'',
    cuisine_type:'', meal_types:[], price_range:2,
    address:'', neighbourhood:'', description:'',
    website:'', instagram_handle:''
  });

  const setL = (k,v) => setLead(p=>({...p,[k]:v}));
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));
  const toggleMealType = (t) => setF('meal_types', form.meal_types.includes(t) ? form.meal_types.filter(x=>x!==t) : [...form.meal_types,t]);

  const handleLead = async (e) => {
    e.preventDefault();
    if (!lead.email) return setError('Email is required');
    setLoading(true); setError('');
    try {
      await submitLead({ ...lead, source:'website' });
      setForm(p=>({ ...p, business_name:lead.business_name, owner_name:lead.owner_name, email:lead.email, phone:lead.phone, cuisine_type:lead.cuisine_type }));
      setStep(2);
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) return setError('Passwords do not match');
    if (form.password.length < 8) return setError('Password must be at least 8 characters');
    setLoading(true); setError('');
    try {
      const res = await registerBusiness(form);
      login(res.token, res.business, 'business');
      navigate('/business/dashboard');
    } catch (err) { setError(err.message || 'Registration failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="biz-auth-page">
      <div className="biz-auth-left">
        <div className="biz-auth-pitch">
          <div className="biz-auth-logo font-display">half<span>price</span>.ca</div>
          <h2>Reach thousands of Winnipeg diners looking for great restaurant deals</h2>
          <ul className="pitch-points">
            <li>✓ First deal listed completely free</li>
            <li>✓ Zero commission — ever</li>
            <li>✓ Live within minutes of registering</li>
            <li>✓ Loyalty stamps bring customers back</li>
            <li>✓ Cancel anytime — no contracts</li>
          </ul>
          <div className="pitch-social">
            <div className="pitch-stat"><strong>4,200+</strong> Winnipeg diners active</div>
            <div className="pitch-stat"><strong>$0</strong> commission taken</div>
          </div>
        </div>
      </div>

      <div className="biz-auth-right">
        {step === 1 ? (
          <div className="auth-form-box fade-up">
            <h1>List your restaurant</h1>
            <p className="auth-sub">Takes 30 seconds. No payment needed to start.</p>
            {error && <div className="auth-error">{error}</div>}
            <form onSubmit={handleLead}>
              <div className="form-group">
                <label>Restaurant name</label>
                <input className="form-control" placeholder="e.g. Pizzeria Gusto" value={lead.business_name} onChange={e=>setL('business_name',e.target.value)} />
              </div>
              <div className="form-group">
                <label>Your name (owner/manager)</label>
                <input className="form-control" placeholder="Full name" value={lead.owner_name} onChange={e=>setL('owner_name',e.target.value)} />
              </div>
              <div className="form-group">
                <label>Email address *</label>
                <input type="email" className="form-control" placeholder="you@yourrestaurant.ca" value={lead.email} onChange={e=>setL('email',e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Phone (optional)</label>
                <input className="form-control" placeholder="204-555-0100" value={lead.phone} onChange={e=>setL('phone',e.target.value)} />
              </div>
              <div className="form-group">
                <label>Cuisine type</label>
                <select className="form-control" value={lead.cuisine_type} onChange={e=>setL('cuisine_type',e.target.value)}>
                  <option value="">Select cuisine…</option>
                  {CUISINES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                </select>
              </div>
              <button type="submit" className="btn btn-primary w-full" disabled={loading} style={{justifyContent:'center',padding:'13px'}}>
                {loading ? 'Saving…' : 'Get started free →'}
              </button>
            </form>
            <p className="auth-switch">Already registered? <Link to="/business/login">Log in</Link></p>
          </div>
        ) : (
          <div className="auth-form-box fade-up">
            <div className="success-step-banner">✓ Almost there — create your account to go live</div>
            <h2>Create your account</h2>
            <p className="auth-sub">Your restaurant goes live within minutes of registering.</p>
            {error && <div className="auth-error">{error}</div>}
            <form onSubmit={handleRegister}>
              <div className="form-row">
                <div className="form-group">
                  <label>Restaurant name *</label>
                  <input className="form-control" value={form.business_name} onChange={e=>setF('business_name',e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Owner/manager name *</label>
                  <input className="form-control" value={form.owner_name} onChange={e=>setF('owner_name',e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label>Email address *</label>
                <input type="email" className="form-control" value={form.email} onChange={e=>setF('email',e.target.value)} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password *</label>
                  <input type="password" className="form-control" placeholder="Min 8 characters" value={form.password} onChange={e=>setF('password',e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Confirm password *</label>
                  <input type="password" className="form-control" value={form.confirmPassword} onChange={e=>setF('confirmPassword',e.target.value)} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Cuisine type *</label>
                  <select className="form-control" value={form.cuisine_type} onChange={e=>setF('cuisine_type',e.target.value)} required>
                    <option value="">Select…</option>
                    {CUISINES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Price range</label>
                  <select className="form-control" value={form.price_range} onChange={e=>setF('price_range',Number(e.target.value))}>
                    {PRICE_RANGES.map(p=><option key={p.v} value={p.v}>{p.l}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Meal types offered</label>
                <div className="meal-type-row">
                  {MEAL_TYPES.map(t=>(
                    <button type="button" key={t} className={`meal-type-btn ${form.meal_types.includes(t)?'active':''}`} onClick={()=>toggleMealType(t)}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Neighbourhood</label>
                  <select className="form-control" value={form.neighbourhood} onChange={e=>setF('neighbourhood',e.target.value)}>
                    <option value="">Select…</option>
                    {HOODS.map(h=><option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="form-control" placeholder="204-555-0100" value={form.phone} onChange={e=>setF('phone',e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input className="form-control" placeholder="123 Main St, Winnipeg, MB" value={form.address} onChange={e=>setF('address',e.target.value)} />
              </div>
              <div className="form-group">
                <label>About your restaurant</label>
                <textarea className="form-control" rows={3} placeholder="Tell diners what makes your restaurant special…" value={form.description} onChange={e=>setF('description',e.target.value)} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Website (optional)</label>
                  <input className="form-control" placeholder="https://yourrestaurant.ca" value={form.website} onChange={e=>setF('website',e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Instagram handle (optional)</label>
                  <input className="form-control" placeholder="@yourrestaurant" value={form.instagram_handle} onChange={e=>setF('instagram_handle',e.target.value.replace('@',''))} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary w-full" disabled={loading} style={{justifyContent:'center',padding:'13px'}}>
                {loading ? 'Creating account…' : 'Create account & go live →'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
